/* Segments: a question asked of the roster, not a stored list.
 *
 * A card is a count, and by SITE-RULES 11e a count must open its names, so every card
 * is a button and the modal behind it is the people. The score in that modal is the
 * score everywhere else - one model - and anybody the model declines to score reads
 * "Not scored" with the reason beside it rather than a number that would be a guess.
 *
 * ONE CARD IS NOT AN AUDIENCE. Care Track and Hold is a REVIEW list: it exists so
 * nobody is forgotten, which is the opposite of a send list, and it says so on itself.
 */
import { useState } from 'react';
import type { Member } from '@acrostic/model';
import { notScoredBecause } from '@acrostic/model';
import { useAsync, useSource } from '../data/SourceProvider.js';
import type { SegmentView } from '../data/contract.js';
import { NoneRow } from '../components/Panel.js';
import { Modal } from '../components/Modal.js';
import { Avatar } from '../components/Avatar.js';
import { tone } from '../components/People.js';
import { fmt } from '../lib/fmt.js';
import { go } from '../routes.js';

export function Segments() {
  const source = useSource();
  const { data, error } = useAsync(() => source.getSegments(), [source]);
  const [open, setOpen] = useState<SegmentView | null>(null);

  return (
    <>
      <p className="t-body-small muted">
      </p>

      {error && <NoneRow>Could not read the segments. {error.message}</NoneRow>}
      {!error && !data && <NoneRow>Counting…</NoneRow>}

      {data && (
        <div className="grid2">
          {data.map((s) => (
            <button key={s.id} type="button" className="panel segcard" onClick={() => setOpen(s)}>
              <h3 className="t-heading-h3">{s.name}</h3>
              <div className="t-display-xsmall">{fmt.num(s.n)}</div>
              <p className="t-body-small muted">{s.desc}</p>
              {!s.sendable && (
                <span className="pill flat t-caption" style={{ marginTop: 6 }}>Never an audience</span>
              )}
            </button>
          ))}
        </div>
      )}

      {open && <SegmentPeople s={open} onClose={() => setOpen(null)} />}
    </>
  );
}

const PAGE = 25;

function SegmentPeople({ s, onClose }: { s: SegmentView; onClose: () => void }) {
  const source = useSource();
  const [page, setPage] = useState(1);
  const { data } = useAsync(
    () => source.getSegmentMembers(s.id, { page, size: PAGE }), [source, s.id, page]);
  const from = (page - 1) * PAGE + 1;

  return (
    <Modal
      title={`${s.name} · ${fmt.num(s.n)} ${s.n === 1 ? 'member' : 'members'}`}
      onClose={onClose}
      footer={<button type="button" className="btn t-label-medium" onClick={onClose}>Done</button>}
    >
      <p className="t-body-small muted">{s.desc}</p>
      {!data ? <NoneRow>Reading…</NoneRow>
        : !data.total ? <NoneRow>Nobody is in this segment right now.</NoneRow>
        : (
          <>
            <div className="rows register">
              {/* Risk-ranked segments arrive worst first, because the score counts down
                  from 100. The page preserves that order rather than re-sorting it. */}
              {data.items.map((m) => (
                <Row key={m.id} m={m} onOpen={() => { onClose(); go('/members/' + m.id); }} />
              ))}
            </div>
            {data.total > PAGE && (
              <div className="pager t-label-small">
                <button type="button" className="btn" disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}>Previous</button>
                <span className="muted">
                  {from}–{from + data.items.length - 1} of {fmt.num(data.total)}
                </span>
                <button type="button" className="btn" disabled={page * PAGE >= data.total}
                  onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}
    </Modal>
  );
}

function Row({ m, onOpen }: { m: Member; onOpen: () => void }) {
  const off = notScoredBecause(m);
  const score = off ? null : m.risk?.score ?? null;
  return (
    <button type="button" className="row clickable" onClick={onOpen}>
      <Avatar name={m.name} photoUrl={m.photoUrl} size="md" />
      <div className="who">
        <div className="n t-label-large">{m.name}</div>
        <div className="s t-caption muted">
          {[m.plan, m.lastVisit ? 'last visit ' + fmt.ago(m.lastVisit) : 'no visit on record']
            .filter(Boolean).join(' · ')}
        </div>
      </div>
      {/* The one model, or an honest reason there is no number. Never a stale one. */}
      {score == null
        ? <span className="pill flat t-caption" title={off ?? ''}>{off ?? 'Not scored'}</span>
        : <span className={'badge ' + tone(score) + ' t-label-medium'}>{score}</span>}
    </button>
  );
}
